import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import CONSTANT from "../../../../constants/Constant";
import routes from "../../../../constants/Routes";
import { getMarkup } from "../../../../utils/Utils";
import InputBox from "../../form-element/InputBox";
import RadioButton from "../../form-element/RadioButton";
import MaskInput from "../../form-element/MaskInput";
import DropdownItem from "../../form-element/DropdownItem";
import CheckboxForEditAssessment from "../../form-element/CheckboxForEditAssessment";
import useNewChatbotBody from "../hooks/useNewChatbotBody";
import useChatbotAudio from "../hooks/useChatbotAudio";
import { CC_RESPONSE_TYPE } from "../../../../features/chatbotNew/chatbotNewUtils";
import {
  setCurrentQuestionIndex,
  setQuestionAnswerInNew,
  advanceToNextQuestion,
} from "../../../../features/chatbotNew/chatbotNewSlice";
import ChiefComplaintWithBodyPart from "../../chiefComplaintNew/ChiefComplaintWithBodyPart";
import GLOBAL from "../../../../global";
import ExamService from "../../../../services/ExamService";
import BiometricPrompt from "../BiometricPrompt/BiometricPrompt";

const NewCurrentQuestion = () => {
  const chatBotNewState = useSelector((state) => state.chatBotNew);
  const {
    flatQuestions,
    currentQuestionIndex,
    isSaving,
    testId,
    showBiometricPrompt,
  } = chatBotNewState;
  const {
    handleNewChatbotAnswerSave,
    handleOKForTextQuestion,
    registerStopAudio,
  } = useNewChatbotBody();
  const { stopAudio } = useChatbotAudio();
  const dispatch = useDispatch();

  const [ccModalOpen, setCcModalOpen] = useState(false);
  const [validationError, setValidationError] = useState("");

  useEffect(() => {
    setValidationError("");
  }, [currentQuestionIndex]);

  // Register stopAudio with parent hook
  useEffect(() => {
    if (registerStopAudio) {
      registerStopAudio(stopAudio);
    }
  }, [stopAudio, registerStopAudio]);

  const question = flatQuestions[currentQuestionIndex];

  // Check if question is not editable (UserEditResponse: false from API)
  const isNotEditable = question?.groupId === CONSTANT.ASSESSMENT_GROUP.BIOMETRIC.key ? false : question?.UserEditResponse === false;

  if (!question || isSaving === undefined) return null;

  if (isSaving) {
    return (
      <div className="mmh-chat__text mmh-font-xl current-question">
        <div className="current-question-container">
          <div className="question">
            <div
              className="displayFlex justify-center mmh-align-items-center"
              style={{ padding: "2rem" }}
            >
              <i className="fa fa-spinner fa-spin fa-2x" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  const {
    responseType,
    text,
    dialogue,
    responses,
    answerId,
    answerValue,
    answerList,
  } = question;

  const isTextType =
    responseType === CONSTANT.ELEMENT_TYPES.TEXT ||
    responseType === CONSTANT.ELEMENT_TYPES.NUMBER ||
    responseType === CONSTANT.ELEMENT_TYPES.CALCULATION;

  const isDateType =
    responseType === CONSTANT.ELEMENT_TYPES.DATE_PICKER ||
    (responseType === CONSTANT.ELEMENT_TYPES.TEXT &&
      (question.questionId === CONSTANT.CHATBOT_ACCOUNT?.DATE_OF_BIRTH ||
        question.questionId === CONSTANT.CHATBOT_ACCOUNT?.NEXT_KEY_EVENT_DATE));

  const isCheckbox =
    responseType === CONSTANT.ELEMENT_TYPES.CHECKBOX ||
    responseType === CONSTANT.ELEMENT_TYPES.MULTISELECT;

  const isDropdown = responseType === CONSTANT.ELEMENT_TYPES.DROPDOWN;
  const isButton = responseType === CONSTANT.ELEMENT_TYPES.BUTTON;
  const isCCModal = responseType === CC_RESPONSE_TYPE;

  const showOKButton =
    (isTextType && !isDateType) || isDateType || isCheckbox || isDropdown;

  const getFilteredResponses = () => {
    const allResponses = responses || [];
    const hasDependency = allResponses.some(
      (r) => r.dependsOnAnswerId && r.dependsOnAnswerId !== 0,
    );
    if (!hasDependency) return allResponses;

    const prevQuestion =
      currentQuestionIndex > 0 ? flatQuestions[currentQuestionIndex - 1] : null;
    const prevAnswerId = prevQuestion?.answerId;

    return allResponses.filter(
      (r) =>
        !r.dependsOnAnswerId ||
        r.dependsOnAnswerId === 0 ||
        r.dependsOnAnswerId === prevAnswerId,
    );
  };

  const handleCCModalClose = () => {
    setCcModalOpen(false);

    // If user closed modal without selecting any Chief Complaints,
    // change answer to "No, Chief Complaints"
    if (!GLOBAL.ISANYCHIEFCOMPLAINTANSWEDED) {
      const question = flatQuestions[currentQuestionIndex];
      const noResponse = (question.responses || []).find(r => r.id === 61369 || (r.name || '').toLowerCase().includes('no'));

      if (noResponse) {
        const value = {
          answerId: noResponse.id,
          answerText: noResponse.name,
          question: question
        };
        handleNewChatbotAnswerSave(value);
      }

      // Save to API
      ExamService.saveNoCC_Complaint({ TestId: testId }).catch((error) => {
        console.error("Error saving No Chief Complaints:", error);
      });
    }
  };

  const handleCCModalSubmit = async () => {
    stopAudio();
    setCcModalOpen(false);

    // Check if user selected any Chief Complaints
    if (!GLOBAL.ISANYCHIEFCOMPLAINTANSWEDED) {
      // No complaints selected - set answer to "No, Chief Complaints"
      const question = flatQuestions[currentQuestionIndex];
      const noResponse = (question.responses || []).find(r => r.id === 61369 || (r.name || '').toLowerCase().includes('no'));

      if (noResponse) {
        const value = {
          answerId: noResponse.id,
          answerText: noResponse.name,
          question: question
        };
        await handleNewChatbotAnswerSave(value);

        // Save to specialized API
        try {
          await ExamService.saveNoCC_Complaint({ TestId: testId });
        } catch (error) {
          console.error("Error saving No Chief Complaints:", error);
        }

        dispatch(advanceToNextQuestion());
        return;
      }
    }

    // Regular advance for when complaints were selected (Yes path)
    dispatch(advanceToNextQuestion());
  };

  // Wrapper to stop audio before handling answer save
  const handleAnswerSaveWithStop = async (value) => {
    stopAudio();
    setValidationError("");

    // Check if this is the CC "No Chief Complaints" response (AnswerId 61369)
    if (
      value?.question?.responseType === CC_RESPONSE_TYPE &&
      value?.answerId === 61369
    ) {
      // Special handling for "No Chief Complaints"
      const question = value.question;

      // Update the answer in Redux state
      dispatch(
        setQuestionAnswerInNew({
          questionId: question.questionId,
          answerId: value.answerId,
          answerValue: value.answerText || value.answerTitle,
        }),
      );

      // Save the primary answer first (triggers SaveAssessment API like other questions)
      await handleNewChatbotAnswerSave(value);

      // Call the special saveNoCC_Complaint API after the primary save
      try {
        await ExamService.saveNoCC_Complaint({ TestId: testId });
        GLOBAL.ISANYCHIEFCOMPLAINTANSWEDED = false;
      } catch (error) {
        console.error("Error saving No Chief Complaints:", error);
      }
      dispatch(advanceToNextQuestion());
      return;
    }

    // Check if this is the CC "Yes" response (AnswerId 61368)
    // Save the answer first, then open modal without advancing
    if (
      value?.question?.responseType === CC_RESPONSE_TYPE &&
      value?.answerId === 61368
    ) {
      await handleNewChatbotAnswerSave(value);
      setCcModalOpen(true);
    } else {
      await handleNewChatbotAnswerSave(value);
    }
  };

  // Wrapper to stop audio before handling OK button
  const handleOKWithStop = async () => {
    stopAudio();
    const result = await handleOKForTextQuestion();
    if (result && !result.valid) {
      setValidationError(result.message || "This field is required.");
    } else {
      setValidationError("");
    }
  };

  // Render BiometricPrompt if flag is set
  // Note: BiometricPrompt handles MovementModal rendering internally
  if (showBiometricPrompt) {
    return <BiometricPrompt />;
  }

  if (isCCModal) {
    return (
      <div className="mmh-chat__text mmh-font-xl current-question">
        <div className="current-question-container">
          <div className="question">
            {(question.groupName || question.header1) && (
              <div className="heading">
                <span className="mmh-text-left">
                  {question.groupName && <span>{question.groupName}</span>}
                  {question.header1 && (
                    <>
                      <i
                        className="fas fa-chevron-right"
                        style={{ margin: "0 0.5rem" }}
                      />
                      <span>{question.header1}</span>
                    </>
                  )}
                </span>
              </div>
            )}
            <div className="question__text mmh-d-inline-block">
              <span
                dangerouslySetInnerHTML={getMarkup(
                  question.questionTitle || text || "",
                )}
              />
            </div>
            <div className="current-question__line-break" />

            {/* Render the two response buttons (Yes/No) */}
            {responses && responses.length > 0 && (
              <RadioButton
                label=""
                question={question}
                options={getFilteredResponses()}
                checkedOption={answerId}
                onChange={handleAnswerSaveWithStop}
                cssStyle="column"
                disabled={isNotEditable}
              />
            )}
          </div>
        </div>

        {/* Chief Complaints Modal - only opens when "Yes" is selected */}
        <ChiefComplaintWithBodyPart
          chat={null}
          options={{}}
          isOpen={ccModalOpen}
          onClose={handleCCModalClose}
          onSubmit={handleCCModalSubmit}
          ccQuestion={question}
          onChange={handleAnswerSaveWithStop}
          testId={
            testId || (GLOBAL.ASSESSMENT && GLOBAL.ASSESSMENT["TestId"]) || ""
          }
        />
      </div>
    );
  }

  return (
    <div className="mmh-chat__text mmh-font-xl current-question">
      <div className="current-question-container">
        <div className="question">
          {(question.groupName || question.header1) && (
            <div className="heading">
              <span className="mmh-text-left">
                {question.groupName && <span>{question.groupName}</span>}
                {question.header1 && (
                  <>
                    <i
                      className="fas fa-chevron-right"
                      style={{ margin: "0 0.5rem" }}
                    />
                    <span>{question.header1}</span>
                  </>
                )}
              </span>
            </div>
          )}
          <div className="question__text mmh-d-inline-block">
            <span dangerouslySetInnerHTML={getMarkup(text || "")} />
          </div>
          <div className="current-question__line-break" />

          {isButton && responses && responses.length > 0 && (
            <RadioButton
              label=""
              question={question}
              options={getFilteredResponses()}
              checkedOption={answerId}
              onChange={handleAnswerSaveWithStop}
              cssStyle="column"
              disabled={isNotEditable}
            />
          )}

          {isDropdown && (
            <div className="new-chatbot-dropdown-wrapper">
              <DropdownItem
                label=""
                checkedOption={answerId}
                value={getFilteredResponses()}
                question={question}
                onChange={handleAnswerSaveWithStop}
                disabled={isNotEditable}
              />
            </div>
          )}

          {isCheckbox && responses && responses.length > 0 && (
            <CheckboxForEditAssessment
              label=""
              question={question}
              options={getFilteredResponses()}
              onChange={handleAnswerSaveWithStop}
              checkedOptionsIds={answerList ?? []}
              cssStyle="column"
              disabled={isNotEditable}
            />
          )}

          {isDateType && (
            <div className="new-chatbot-dropdown-wrapper">
              <MaskInput
                label=""
                value={answerValue}
                placeHolder="MM/DD/YYYY"
                question={question}
                mask="99/99/9999"
                onChange={handleAnswerSaveWithStop}
                disabled={isNotEditable}
              />
            </div>
          )}

          {isTextType && !isDateType && (
            <div className="new-chatbot-dropdown-wrapper">
              <InputBox
                label=""
                placeHolder={dialogue}
                value={answerValue}
                question={question}
                onChange={handleAnswerSaveWithStop}
                disabled={isNotEditable}
              />
            </div>
          )}

          {validationError && (
            <div
              className="chatbot-validation-error"
              style={{
                color: "#dc3545",
                fontSize: "0.875rem",
                marginTop: "0.5rem",
              }}
            >
              {validationError}
            </div>
          )}

          {showOKButton && (
            <button
              type="button"
              className="ok-btn mmh-mt-10"
              onClick={handleOKWithStop}
            >
              OK
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default NewCurrentQuestion;
